import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X } from "lucide-react";
import "./SignInForm.css";

interface ForgotPasswordModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function ForgotPasswordModal({
  isOpen,
  onClose,
}: ForgotPasswordModalProps) {
  const [email, setEmail] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setSuccess("");

    if (!/^\S+@\S+\.\S+$/.test(email)) {
      setError("Please enter a valid email address.");
      return;
    }

    try {
      setLoading(true);
      const response = await fetch(
        `${import.meta.env.VITE_API_BASE_URL}/auth/forgot-password`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ email }),
        }
      );

      const data = await response.json().catch(() => ({} as { message?: string }));

      if (response.ok && data.success) {
        setSuccess(data.message || "A password reset link has been sent to your email.");
        setEmail("");

        setTimeout(() => {
          setSuccess("");
          onClose();
        }, 3500);
      } else if (response.status === 404) {
        setError(
          data.message ||
            "This email address is not registered. Please check your email and try again."
        );
      } else if (response.status === 503) {
        setError(
          data.message ||
            "Email service is currently unavailable. Please try again later or contact support."
        );
      } else {
        setError(data.message || "Failed to send reset link.");
      }
    } catch (error) {
      console.error("Forgot password error:", error);
      setError("Network error. Please check your connection and try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className="fixed inset-0 z-50 flex items-center justify-center bg-[#2A1F17]/55 backdrop-blur-sm p-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          <motion.div
            className="login-glass-card relative w-full max-w-md rounded-2xl p-6"
            initial={{ y: 12, opacity: 0, scale: 0.97 }}
            animate={{ y: 0, opacity: 1, scale: 1 }}
            exit={{ y: 12, opacity: 0, scale: 0.97 }}
            transition={{ duration: 0.2, ease: "easeOut" }}
          >
            {/* Close Button */}
            <button
              onClick={onClose}
              className="absolute top-4 right-4 z-10 text-[#5A6B65] hover:text-[#0B3B2E] transition"
              aria-label="Close modal"
            >
              <X size={20} />
            </button>

            {/* Header */}
            <div className="flex flex-col items-center mb-4">
              <h2 className="text-xl font-bold text-[#0B3B2E] tracking-tight text-center">
                Forgot your password?
              </h2>
              <p className="text-sm text-[#5A6B65] mt-1 text-center">
                Enter your email and we'll send you a reset link.
              </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4 mt-4">
              {!success && (
                <>
                  <div
                    className={`relative flex items-center h-14 px-4 rounded-xl transition-all duration-300 w-full
                      ${
                        error
                          ? "bg-red-50/70 border border-error-500 ring-2 ring-error-500/20"
                          : "login-glass-input"
                      }`}
                  >
                    <svg
                      width="22"
                      height="22"
                      viewBox="0 0 30 30"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                      className="shrink-0"
                    >
                      <path
                        d="M4.95289 9.71255C4.7179 10.6695 4.54627 11.9052 4.30751 13.6244C3.76229 17.5499 3.48969 19.5126 4.08909 21.0371C4.61531 22.3754 5.58692 23.4913 6.8401 24.1966C8.26756 25 10.2492 25 14.2124 25H16.2426C20.2059 25 22.1875 25 23.615 24.1966C24.8681 23.4913 25.8397 22.3754 26.366 21.0371C26.9654 19.5126 26.6927 17.5499 26.1476 13.6244C25.8882 11.7573 25.7081 10.4604 25.44 9.47159L24.527 10.3846C22.4866 12.425 20.8877 14.024 19.4724 15.1039C18.0237 16.2091 16.6579 16.8585 15.076 16.8585C13.4941 16.8585 12.1283 16.2091 10.6796 15.1039C9.26427 14.024 7.66531 12.425 5.62497 10.3846L4.95289 9.71255Z"
                        fill="#0B3B2E"
                      />
                      <path
                        d="M5.71875 7.82637L6.90045 9.00807C9.00253 11.1101 10.5138 12.6186 11.8173 13.6131C13.0993 14.5912 14.0694 14.9835 15.0764 14.9835C16.0834 14.9835 17.0535 14.5912 18.3354 13.6131C19.639 12.6186 21.1503 11.1101 23.2523 9.00807L24.6144 7.64595C23.9795 6.7463 23.1169 6.02886 22.1128 5.56854C20.8725 5 19.3294 5 16.243 5H14.2127C11.1265 5 9.58333 5 8.34313 5.56854C7.27274 6.05924 6.3632 6.84209 5.71875 7.82637Z"
                        fill="#0B3B2E"
                      />
                    </svg>
                    <input
                      type="email"
                      placeholder="you@sorigin.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      disabled={loading}
                      className="flex-1 bg-transparent border-none outline-none text-[#0B3B2E] placeholder-[#7C9286] text-sm h-full ml-3"
                    />
                  </div>

                  {error && <p className="text-sm text-error-500 mt-1">{error}</p>}

                  <motion.button
                    type="submit"
                    disabled={loading}
                    whileHover={!loading ? { scale: 1.015, y: -1 } : undefined}
                    whileTap={!loading ? { scale: 0.985 } : undefined}
                    className="login-glass-btn relative w-full h-14 rounded-xl text-white font-semibold tracking-wide disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    {loading ? "Sending…" : "Send reset link"}
                  </motion.button>
                </>
              )}

              {success && (
                <motion.div
                  className="flex flex-col items-center justify-center mt-2"
                  initial={{ scale: 0.95, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  transition={{ duration: 0.25, ease: "easeOut" }}
                >
                  <div className="rounded-full bg-[#A6CE39]/25 p-3">
                    <svg
                      className="text-[#076333]"
                      width="28"
                      height="28"
                      viewBox="0 0 24 24"
                      fill="none"
                    >
                      <path
                        d="M9 12l2 2l4 -4"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="2" />
                    </svg>
                  </div>
                  <p className="text-sm text-[#076333] font-medium mt-2 text-center">
                    {success}
                  </p>
                </motion.div>
              )}
            </form>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
